import React, { useEffect } from 'react';
import {
  Avatar,
  Badge,
  Button,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import FormInput from 'components/FormInput';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RootStates } from 'redux/store';
import { FormDropdown } from 'components/common/FormDropdown';
import { BLOOD_GROUP } from './constants';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { getBase64File, getHeaders } from 'helper/helper';
import { Edit } from '@mui/icons-material';
import { editProfileSchema } from './schema';
import { useUpdateUserDetailsMutation } from 'redux/services/auth';
import { useUploadAssetMutation } from 'redux/services/project';
import './EditProfile.scss';
import { Loader } from 'components/common/Loader';
import { dataURItoBlob } from 'components/common/FormCaptureInput/utils';
import { FormCaptureInput } from 'components/common/FormCaptureInput';
import { saveUserDetails } from 'redux/authSlice';

const EditProfile = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // const [otherValue, setOtherValue] = useState('');
  // const [showOtherValueDialog, setShowOtherValueDialog] = useState(false);
  const [uploadAsset, { isLoading }] = useUploadAssetMutation();
  const [updateUserDetails, { isLoading: updating }] =
    useUpdateUserDetailsMutation();
  const user = useSelector((state: RootStates) => state.auth.userDetails);

  const uploadImage = async (base64String: string, path: string) => {
    return new Promise(async (resolve) => {
      if (base64String) {
        const formData = new FormData();
        formData.append('file', dataURItoBlob(base64String));
        formData.append('path', path);
        const res: any = await uploadAsset(formData);

        if (res.data) {
          resolve(res.data.url);
        } else {
          toast.error(res.error.data.message || 'Something went wrong');
          resolve('');
        }
      } else {
        resolve('');
      }
    });
  };

  const getDefaultCompanySize = React.useCallback(() => {
    if (user.onboardingData?.companySize) {
      return `${user.onboardingData.companySize.lower}-${user.onboardingData.companySize.upper}`;
    }

    return '';
  }, [user?.onboardingData?.companySize]);

  const formik = useFormik({
    initialValues: {
      name: user.name,
      organization: user.onboardingData?.organization ?? '',
      email: user.email,
      mobile: user.mobile,
      bloodGroup: user.bloodGroup,
      signature: user.signature,
      imageUrl: user.imageUrl,
      whatsAppUpdatesEnabled: user.whatsAppUpdatesEnabled ?? false,
      companyLogo: user.companyLogo,
    },
    validationSchema: editProfileSchema,
    onSubmit: async () => {
      const values = formik.values;

      const headers = await getHeaders();
      const imageUrl: any = await uploadImage(
        values.imageUrl,
        `profiles/${user._id}`,
      );

      const res: any = await updateUserDetails({
        headers,
        body: {
          name: values.name,
          bloodGroup: values.bloodGroup,
          imageUrl: imageUrl || user.imageUrl || null,
          whatsAppUpdatesEnabled: values.whatsAppUpdatesEnabled,
          companyLogo: values.companyLogo || user.companyLogo || null,
        },
      });

      if (res.data) {
        toast.success(t('save_success'));
        dispatch(saveUserDetails(res.data));
        navigate(-1);
      } else {
        toast.error(res.error.data.message || t('something_went_wrong'));
      }
    },
  });

  useEffect(() => {
    if (!formik.values.name && (user.name || user.mobile || user.email)) {
      formik.setValues({
        name: user.name,
        organization: user.onboardingData?.organization ?? '',
        email: user.email,
        mobile: user.mobile,
        bloodGroup: user.bloodGroup,
        signature: user.signature,
        imageUrl: user.imageUrl,
        whatsAppUpdatesEnabled: user.whatsAppUpdatesEnabled ?? false,
        companyLogo: user.companyLogo,
      });
    }
  }, [user, formik, getDefaultCompanySize]);

  const handleSelectBloodGroup = (item: { name: string }) => {
    formik.setFieldValue('bloodGroup', item.name);
  };

  const handleAvatarChange = async (e: any) => {
    const file = await getBase64File(e.target.files[0]);
    const fileSize = e.target.files[0].size / 1024 ** 2;

    if (fileSize <= 1) {
      formik.setFieldValue('imageUrl', file);
    } else {
      toast.error(t('error_file_size_exceeded'));
    }
  };

  const handleBack = () => navigate(-1);

  // const handleCopyContactChange = (
  //   event: React.ChangeEvent<HTMLInputElement>,
  // ) => {
  //   const checked = event.target.checked;

  //   if (checked) {
  //     formik.setFieldValue('whatsAppNumber', formik.values.mobile);
  //   } else {
  //     formik.setFieldValue('whatsAppNumber', '');
  //   }

  //   formik.setFieldValue('copyContact', checked);
  // };

  const handleCapture = (url: string) => formik.setFieldValue('signature', url);

  const handleDeleteImage = () => {
    formik.setFieldValue('signature', '');
  };

  // const getSelectCompanyNatureHandler = (nature: CompanyNature) => () => {
  //   if (nature.value === '') {
  //     setShowOtherValueDialog(true);
  //   }
  //   formik.setFieldValue('companyNature', nature);
  // };

  // const handleCloseOtherCompanyNatureDialog = () => {
  //   formik.setFieldValue('companyNature', {});
  //   setShowOtherValueDialog(false);
  // };

  // const handleSubmitOtherCompanyNatureDialog = () => {
  //   formik.setFieldValue('companyNature', {
  //     ...formik.values.companyNature,
  //     value: otherValue,
  //   });
  //   setShowOtherValueDialog(false);
  // };

  // const handleChangeOtherCompanyNatureInput = (
  //   event: React.ChangeEvent<HTMLInputElement>,
  // ) => {
  //   setOtherValue(event.target.value);
  // };

  // const handleSelect = (event: React.ChangeEvent<HTMLSelectElement>) => {
  //   formik.setFieldValue('companySize', event.target.value.trim());
  // };

  return (
    <div className="edit-profile d-flex flex-column w-100">
      <h4 className="page-title">{t('header_edit_profile')}</h4>
      <form
        className="d-flex setup-container flex-column"
        onSubmit={formik.handleSubmit}
      >
        <div className="d-flex flex-grow-1 flex-column w-100 overflow-auto">
          <div className="d-flex">
            <FormInput
              label={t('label_name')}
              required
              className="flex-grow-1"
              name="name"
              error={formik.errors.name}
              value={formik.values.name}
              onChange={formik.handleChange}
            />
            <div
              style={{
                marginLeft: 24,
                marginRight: 24,
                cursor: 'pointer',
              }}
            >
              <label htmlFor="avatarImg">
                <Badge
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  overlap="circular"
                  badgeContent={
                    <div
                      style={{
                        background: 'white',
                        borderRadius: '50%',
                        cursor: 'pointer',
                        padding: 3,
                        boxShadow: '1px 1px 5px 0 #604596',
                      }}
                    >
                      <Edit />
                    </div>
                  }
                >
                  <Avatar
                    sx={{
                      color: '#fff',
                      background: '#4b2988',
                      cursor: 'pointer',
                      width: 78,
                      height: 78,
                      border: '1px solid #4b2988',
                      fontSize: '2.5rem',
                    }}
                    alt={formik.values.name}
                    src={formik.values.imageUrl}
                  >
                    {`${formik.values.name.split(' ')[0]?.[0] ?? ''}${
                      formik.values.name.split(' ')[1]?.[0] ?? ''
                    }`}
                  </Avatar>
                </Badge>
                <input
                  accept={'image/*'}
                  className="d-none"
                  data-testid="input-file"
                  id="avatarImg"
                  type="file"
                  onChange={handleAvatarChange}
                />
              </label>
            </div>
          </div>
          <FormInput
            label={t('label_organization_name')}
            required={false}
            name="organization"
            value={formik.values.organization}
            error={formik.errors.organization}
            onChange={formik.handleChange}
          />
          <FormInput
            label={t('email')}
            disabled
            name="email"
            value={formik.values.email}
            onChange={formik.handleChange}
          />
          <FormInput
            label={t('mobile')}
            disabled
            name="mobile"
            value={formik.values.mobile}
            onChange={formik.handleChange}
          />
          <FormDropdown
            lable={t('label_blood_group')}
            className="mb-4"
            items={BLOOD_GROUP}
            selectedItem={formik.values.bloodGroup}
            onSelect={handleSelectBloodGroup}
          />
          {/* <FormControlLabel
            className="p-1"
            control={
              <Checkbox
                name="whatsAppUpdatesEnabled"
                color="primary"
                checked={formik.values.whatsAppUpdatesEnabled}
                onChange={formik.handleChange}
              />
            }
            label={t('label_whats_app')}
          /> */}
          <FormCaptureInput
            images={[formik.values.signature].filter((img) => img)}
            limit={1}
            label={t('upload_company_logo')}
            path={`logo/${user._id}`}
            onCapture={handleCapture}
            onDelete={handleDeleteImage}
          />
          {/* <div className="d-flex align-items-center justify-content-center">
            <FormInput
              label=""
              placeholder={t('placehold_whatsapp')}
              name="whatsAppNumber"
              className="flex-grow-1"
              type="number"
              value={formik.values.whatsAppNumber}
              onChange={formik.handleChange}
            />
            <FormControlLabel
              className="p-1 mb-3 mx-2 copy-contact-control"
              control={
                <Checkbox
                  name="copyContact"
                  color="primary"
                  checked={formik.values.copyContact}
                  onChange={handleCopyContactChange}
                />
              }
              label={`(${t('label_copy_contact_no')})`}
            />
          </div> */}
          {/* <div className="company-size-input my-3">
            <label>{t('label_company_size')}</label>
            <select
              className="form-select"
              value={formik.values.companySize}
              onChange={handleSelect}
            >
              <option />
              {user.defaultOptions?.companySize.map(({ lower, upper }) => (
                <option key={`${lower}-${upper}`} value={`${lower}-${upper}`}>
                  {`${lower}-${upper}`}
                </option>
              ))}
            </select>
          </div>
          <span className="mb-3">{t('onboardng_additional_info')}</span>
          <div className="btn-container">
            {user.defaultOptions?.companyNature.map((val) => (
              <Button
                key={val.category}
                className={`btn ${
                  formik.values.companyNature?.value === val.value ||
                  formik.values.companyNature?.category === val.category
                    ? 'active'
                    : ''
                }`}
                onClick={getSelectCompanyNatureHandler(val)}
              >
                {val.value === '' && otherValue ? otherValue : val.category}
              </Button>
            ))}
            {formik.errors.companyNature && (
              <span className="error">
                {t('onboarding_select_company_nature')}
              </span>
            )}
          </div> */}
        </div>
        <footer className="d-flex w-100 p-0 align-items-center flex-row justify-content-between flex-grow-0">
          <Button variant="outlined" color="primary" onClick={handleBack}>
            {t('btn_back')}
          </Button>
          <Button variant="contained" color="primary" type="submit">
            {t('btn_save')}
          </Button>
        </footer>
      </form>
      {/* <Modal
        show={showOtherValueDialog}
        submitBtnText={t('btn_ok')}
        modalClassName="other-value-popup"
        title={t('onboarding_company_nature_title')}
        footerContentAlignment="center"
        onClose={handleCloseOtherCompanyNatureDialog}
        onSubmit={handleSubmitOtherCompanyNatureDialog}
      >
        <FormInput
          name="other"
          value={otherValue}
          label={t('label_company_nature')}
          onChange={handleChangeOtherCompanyNatureInput}
        />
      </Modal> */}
      {(isLoading || updating) && <Loader />}
    </div>
  );
};

export default EditProfile;
